/**
 * Utility function to parse rich text JSON and convert it to HTML
 * Handles Lexical editor format and similar rich text structures
 */

export const parseRichTextToHTML = (richTextData) => {
  if (!richTextData) return '';
  
  // If it's already a string, try to parse it
  let parsedData;
  try {
    parsedData = typeof richTextData === 'string' ? JSON.parse(richTextData) : richTextData;
  } catch (error) {
    console.error('Error parsing rich text data:', error);
    return richTextData; // Return original if parsing fails
  }

  // If it's not a Lexical format, return as is
  if (!parsedData.root || !parsedData.root.children) {
    return richTextData;
  }

  const result = parseNode(parsedData.root);
  return result || richTextData; // Fallback to original if parsing fails
};

const parseNode = (node) => {
  if (!node) return '';

  // Handle text nodes
  if (node.type === 'text') {
    let text = node.text || '';
    
    // Apply formatting
    if (node.format) {
      if (node.format & 1) text = `<strong>${text}</strong>`; // Bold
      if (node.format & 2) text = `<em>${text}</em>`; // Italic
      if (node.format & 4) text = `<u>${text}</u>`; // Underline
      if (node.format & 8) text = `<s>${text}</s>`; // Strikethrough
    }
    
    return text;
  }

  // Handle different node types
  switch (node.type) {
    case 'paragraph':
      const paragraphContent = node.children ? node.children.map(parseNode).join('') : '';
      return `<p>${paragraphContent}</p>`;
    
    case 'heading':
      const headingLevel = node.tag || 'h2';
      const headingContent = node.children ? node.children.map(parseNode).join('') : '';
      return `<${headingLevel}>${headingContent}</${headingLevel}>`;
    
    case 'link':
      const linkContent = node.children ? node.children.map(parseNode).join('') : '';
      const href = node.url || '#';
      const target = node.target ? ` target="${node.target}"` : '';
      return `<a href="${href}"${target}>${linkContent}</a>`;
    
    case 'list':
      const listContent = node.children ? node.children.map(parseNode).join('') : '';
      const listTag = node.listType === 'bullet' ? 'ul' : 'ol';
      return `<${listTag}>${listContent}</${listTag}>`;
    
    case 'listitem':
      const listItemContent = node.children ? node.children.map(parseNode).join('') : '';
      return `<li>${listItemContent}</li>`;
    
    case 'quote':
      const quoteContent = node.children ? node.children.map(parseNode).join('') : '';
      return `<blockquote>${quoteContent}</blockquote>`;
    
    case 'code':
      const codeContent = node.children ? node.children.map(parseNode).join('') : '';
      return `<code>${codeContent}</code>`;
    
    case 'codeblock':
      const codeBlockContent = node.children ? node.children.map(parseNode).join('') : '';
      return `<pre><code>${codeBlockContent}</code></pre>`;
    
    case 'linebreak':
      return '<br>';
    
    default:
      // For unknown types, try to process children
      if (node.children) {
        return node.children.map(parseNode).join('');
      }
      return '';
  }
};

// Enhanced parser specifically for Lexical format
export const parseLexicalToHTML = (lexicalData) => {
  if (!lexicalData) return '';
  
  let parsedData;
  try {
    parsedData = typeof lexicalData === 'string' ? JSON.parse(lexicalData) : lexicalData;
  } catch (error) {
    return lexicalData;
  }

  if (!parsedData.root || !parsedData.root.children) {
    return lexicalData;
  }

  const processChildren = (children) => {
    if (!children || !Array.isArray(children)) return '';
    
    return children.map(child => {
      if (child.type === 'text') {
        let text = child.text || '';
        
        // Preserve whitespace and handle special characters
        text = text.replace(/\n/g, '<br>');
        
      // Apply formatting based on format flags
      if (child.format) {
        // Apply formatting in the correct order to avoid conflicts
        if (child.format & 1) text = `<strong>${text}</strong>`; // Bold
        if (child.format & 2) text = `<em>${text}</em>`; // Italic
        if (child.format & 4) text = `<u>${text}</u>`; // Underline
        // Skip strikethrough for links to avoid visual conflicts
        if (child.format & 8 && child.type !== 'link') text = `<s>${text}</s>`; // Strikethrough
      }
        
        return text;
      }
      
      if (child.type === 'link') {
        const linkText = child.children ? processChildren(child.children) : '';
        const href = child.url || '#';
        const target = child.target ? ` target="${child.target}"` : '';
        return `<a href="${href}"${target} class="text-blue hover:text-blue-700 underline font-medium">${linkText}</a>`;
      }
      
      if (child.type === 'paragraph') {
        const paragraphText = child.children ? processChildren(child.children) : '';
        return `<p class="mb-3">${paragraphText}</p>`;
      }
      
      if (child.type === 'heading') {
        const headingLevel = child.tag || 'h2';
        const headingText = child.children ? processChildren(child.children) : '';
        return `<${headingLevel} class="font-bold text-lg mb-2">${headingText}</${headingLevel}>`;
      }
      if (child.type === 'list') {
        const listItems = child.children ? processChildren(child.children) : '';
        const listTag = child.listType === 'bullet' ? 'ul' : 'ol';
        return `<${listTag} class="list-disc list-inside mb-3 space-y-1">${listItems}</${listTag}>`;
      }
      
      if (child.type === 'listitem') {
        const listItemText = child.children ? processChildren(child.children) : '';
        return `<li class="ml-2">${listItemText}</li>`;
      }
      
      if (child.type === 'linebreak') {
        return '<br>';
      }
      
      // For any other type, try to process children
      if (child.children) {
        return processChildren(child.children);
      }
      
      return '';
    }).join('');
  };

  const result = processChildren(parsedData.root.children);
  
  // Clean up extra whitespace and ensure proper spacing
  return result
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/>\s+</g, '><') // Remove spaces between tags
    .replace(/\s+<br>/g, '<br>') // Remove spaces before line breaks
    .replace(/<br>\s+/g, '<br>') // Remove spaces after line breaks
    .replace(/(<br>)+/g, '<br>') // Remove multiple consecutive line breaks
    .replace(/<p><br><\/p>/g, '') // Remove empty paragraphs with line breaks
    .replace(/<p>\s*<\/p>/g, '') // Remove empty paragraphs
    .replace(/<s><u>/g, '<u>') // Remove strikethrough from underlined text
    .replace(/<\/u><\/s>/g, '</u>') // Remove closing strikethrough from underlined text
    .replace(/<s><strong>/g, '<strong>') // Remove strikethrough from bold text
    .replace(/<\/strong><\/s>/g, '</strong>') // Remove closing strikethrough from bold text
    .trim();
};

// Alternative simpler parser for basic text extraction
export const extractTextFromRichText = (richTextData) => {
  if (!richTextData) return '';
  
  let parsedData;
  try {
    parsedData = typeof richTextData === 'string' ? JSON.parse(richTextData) : richTextData;
  } catch (error) {
    return richTextData;
  }

  if (!parsedData.root || !parsedData.root.children) {
    return richTextData;
  }

  const extractText = (node) => {
    if (node.type === 'text') {
      return node.text || '';
    }
    
    if (node.children) {
      return node.children.map(extractText).join('');
    }
    
    return '';
  };

  return extractText(parsedData.root);
};

// Specialized parser for better text formatting and spacing
export const parseRichTextWithFormatting = (richTextData) => {
  if (!richTextData) return '';
  
  let parsedData;
  try {
    parsedData = typeof richTextData === 'string' ? JSON.parse(richTextData) : richTextData;
  } catch (error) {
    return richTextData;
  }

  if (!parsedData.root || !parsedData.root.children) {
    return richTextData;
  }

  const processNode = (node, isFirstInParagraph = false) => {
    if (!node) return '';

    if (node.type === 'text') {
      let text = node.text || '';
      
      // Handle line breaks and preserve spacing
      text = text.replace(/\n/g, '<br>');
      
      // Apply formatting
      if (node.format) {
        // Apply formatting in the correct order to avoid conflicts
        if (node.format & 1) text = `<strong>${text}</strong>`; // Bold
        if (node.format & 2) text = `<em>${text}</em>`; // Italic
        if (node.format & 4) text = `<u>${text}</u>`; // Underline
        // Skip strikethrough for better visual appearance
        if (node.format & 8) {
          // Only apply strikethrough if it's not a link or if it's specifically needed
          text = `<s>${text}</s>`; // Strikethrough
        }
      }
      
      return text;
    }

    if (node.type === 'link') {
      const linkText = node.children ? node.children.map(child => processNode(child)).join('') : '';
      const href = node.url || '#';
      const target = node.target ? ` target="${node.target}"` : '';
      return `<a href="${href}"${target} class="text-blue-500 hover:text-blue-700 underline font-medium">${linkText}</a>`;
    }

    if (node.type === 'paragraph') {
      const paragraphText = node.children ? node.children.map(child => processNode(child)).join('') : '';
      return `<p class="mb-3 leading-relaxed">${paragraphText}</p>`;
    }

    if (node.type === 'heading') {
      const headingLevel = node.tag || 'h2';
      const headingText = node.children ? node.children.map(child => processNode(child)).join('') : '';
      return `<${headingLevel} class="font-bold text-lg mb-2 mt-4">${headingText}</${headingLevel}>`;
    }

    if (node.type === 'linebreak') {
      return '<br>';
    }

    // For any other type, process children
    if (node.children) {
      return node.children.map(child => processNode(child)).join('');
    }

    return '';
  };

  const result = parsedData.root.children.map(child => processNode(child)).join('');
  
  // Clean up the result
  return result
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/>\s+</g, '><') // Remove spaces between tags
    .replace(/(<br>)+/g, '<br>') // Remove multiple consecutive line breaks
    .replace(/<p><br><\/p>/g, '') // Remove empty paragraphs with line breaks
    .replace(/<p>\s*<\/p>/g, '') // Remove empty paragraphs
    .replace(/<s><u>/g, '<u>') // Remove strikethrough from underlined text
    .replace(/<\/u><\/s>/g, '</u>') // Remove closing strikethrough from underlined text
    .replace(/<s><strong>/g, '<strong>') // Remove strikethrough from bold text
    .replace(/<\/strong><\/s>/g, '</strong>') // Remove closing strikethrough from bold text
    .trim();
};
